import { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { teamService } from '@/shared/api/team-service';
import { PrismWordmark } from '@/shared/ui/prism-wordmark';
import { ThemeToggle } from '@/shared/ui/theme-toggle';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import {
  ActivityLogIcon,
  ArchiveIcon,
  BarChartIcon,
  IdCardIcon,
  ChatBubbleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CodeIcon,
  DashboardIcon,
  ExitIcon,
  EyeOpenIcon,
  FileIcon,
  FileTextIcon,
  GearIcon,
  ImageIcon,
  LightningBoltIcon,
  LinkBreak2Icon,
  MagicWandIcon,
  MagnifyingGlassIcon,
  MixerHorizontalIcon,
  PersonIcon,
  ReaderIcon,
  SpeakerLoudIcon,
  StackIcon,
  CardStackIcon,
  CubeIcon,
  TargetIcon,
  VideoIcon,
} from '@radix-ui/react-icons';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { useShallow } from 'zustand/react/shallow';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { ROUTES } from '@/shared/config/routes';
import { useSidebarStore } from '@/shared/stores/sidebar-store';
import { useStorageHealth } from '@/pages/admin/storage-health/hooks/use-storage-health';
import { adminService } from '@/shared/api/admin-service';
import { formatCredits } from '@/shared/utils/credits';
import { useStoriesSeen } from '@/features/stories/seen';
import { useAdsSeen } from '@/pages/dashboard/ads/lib/seen';
import { useSlideshowsSeen } from '@/pages/dashboard/slideshows/lib/seen';
import { useExperimentalFeatures } from '@/shared/hooks/use-experimental-features';


interface MenuItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  /** When set, render a small dot of this color over the icon (e.g. red when storage is unhealthy). */
  alertColor?: 'destructive' | 'warning';
  /** Numeric badge shown next to the label (e.g. pending team count on Teams). Hidden when 0. */
  badgeCount?: number;
  /** Short text tag shown next to the label (e.g. "New"). */
  tag?: string;
}

// Define NavItem component outside to prevent recreation on each render
const NavItem = memo(
  ({
    item,
    isActive,
    isCollapsed,
  }: {
    item: MenuItem;
    isActive: boolean;
    isCollapsed: boolean;
  }) => {
    const dotClass = item.alertColor === 'destructive'
      ? 'bg-destructive'
      : item.alertColor === 'warning'
        ? 'bg-primary'
        : null;
    return (
      <div>
        <Link
          to={item.href}
          className={clsx(
            'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200',
            isActive && 'bg-primary/12 text-primary border-l-2 border-l-primary',
            !isActive && 'text-muted-foreground hover:text-foreground hover:bg-secondary/30',
            isCollapsed && 'justify-center',
          )}
        >
          <motion.span
            className="flex-shrink-0 relative"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {item.icon}
            {dotClass ? (
              <span
                className={clsx(
                  'absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full ring-2 ring-card',
                  dotClass,
                )}
                role="status"
                aria-label={`${item.label}: attention needed`}
              />
            ) : null}
            {!dotClass && item.tag && isCollapsed ? (
              // The text tag is hidden when collapsed; a neutral dot keeps the "new" cue.
              <span
                className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-muted-foreground ring-2 ring-card"
                aria-label={`${item.label}: ${item.tag.toLowerCase()}`}
              />
            ) : null}
          </motion.span>
          <AnimatePresence>
            {!isCollapsed && (
              <motion.span
                className="text-sm font-medium whitespace-nowrap flex-1 flex items-center gap-2"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <span className="flex-1">{item.label}</span>
                {item.tag ? (
                  <Badge variant="outline" className="px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {item.tag}
                  </Badge>
                ) : null}
                {item.badgeCount && item.badgeCount > 0 ? (
                  <span
                    className={clsx(
                      'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5',
                      'rounded-full bg-primary text-primary-foreground text-[11px] font-semibold',
                    )}
                    aria-label={`${item.badgeCount} pending`}
                  >
                    {item.badgeCount > 99 ? '99+' : item.badgeCount}
                  </span>
                ) : null}
              </motion.span>
            )}
          </AnimatePresence>
        </Link>
      </div>
    );
  },
);

NavItem.displayName = 'NavItem';

// Category Header Component with collapsible functionality
const CategoryHeader = memo(
  ({
    label,
    isExpanded,
    onToggle,
    isCollapsed,
  }: {
    label: string;
    isExpanded: boolean;
    onToggle: () => void;
    isCollapsed: boolean;
  }) => {
    return (
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-muted-foreground/60 hover:text-foreground hover:bg-secondary/30 transition-all duration-200"
      >
        <span className="text-[11px] uppercase tracking-widest font-semibold">{!isCollapsed && label}</span>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0"
        >
          <ChevronDownIcon className="w-4 h-4" />
        </motion.div>
      </button>
    );
  },
);

CategoryHeader.displayName = 'CategoryHeader';

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get sidebar state from store
  const { isCollapsed, toggle } = useSidebarStore();

  // State for category collapse/expand (persisted in localStorage)
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem('sidebarExpandedCategories');
    if (saved) {
      try { return JSON.parse(saved); } catch { /* use defaults */ }
    }
    return { dashboard: true, admin: true };
  });

  // Save expanded state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarExpandedCategories', JSON.stringify(expandedCategories));
  }, [expandedCategories]);

  // Toggle category expansion
  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  }, []);

  // Optimize Zustand subscriptions with shallow equality check
  const { user, isAuthenticated, isInitializing } = useAuthStore(
    useShallow((state) => ({
      user: state.user,
      isAuthenticated: state.isAuthenticated,
      isInitializing: state.isInitializing,
    })),
  );

  // Fetch team credits (team-level, not user-level)
  const { data: teams } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamService.getMyTeams(),
    enabled: isAuthenticated,
  });
  const teamCredits = teams?.[0]?.credits;

  // Get logout function separately to avoid re-renders
  const logout = useAuthStore((state) => state.logout);

  // Memoize event handlers to maintain referential equality
  const handleNavigateToProfile = useCallback(() => {
    navigate(ROUTES.PROFILE);
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate(ROUTES.HOME);
  }, [logout, navigate]);

  const handleCreditsClick = useCallback(() => {
    navigate(ROUTES.DASHBOARD_BILLING);
  }, [navigate]);

  const storiesSeen = useStoriesSeen();
  const adsSeen = useAdsSeen();
  const slideshowsSeen = useSlideshowsSeen();
  const experimental = useExperimentalFeatures();
  const dashboardMenuItems: MenuItem[] = useMemo(
    () => [
      {
        label: 'Playground',
        href: ROUTES.DASHBOARD_PLAYGROUND,
        icon: <ImageIcon className="w-5 h-5" />,
      },
      {
        label: 'Quickstart',
        href: ROUTES.DASHBOARD_QUICKSTART,
        icon: <CodeIcon className="w-5 h-5" />,
      },
      {
        // Always visible. When FEATURE_CHARACTERS_ENABLED=false the page
        // itself renders a "Coming soon" state instead of the list — so
        // discoverability stays intact and the nav doesn't shift around
        // mid-pause.
        label: 'Characters',
        href: ROUTES.DASHBOARD_CHARACTERS,
        icon: <PersonIcon className="w-5 h-5" />,
      },
      {
        // Styles gallery (style-registers PR-2 Task 3) — MagicWandIcon
        // matches the gallery's own EmptyState icon (loras/index.tsx) so the
        // nav and the page it opens read as one surface. LightningBoltIcon
        // stays reserved for the ADMIN LoRA registry (:342 below) and the
        // brand mark — a genuinely different, LoRA-vocabulary surface.
        label: 'Styles',
        href: ROUTES.DASHBOARD_LORAS,
        icon: <MagicWandIcon className="w-5 h-5" />,
      },
      // The experimental surfaces (Stories, Ads, Slideshows), shown only while the API runs with
      // EXPERIMENTAL_FEATURES_ENABLED=true. Placed after Styles so existing items keep their positions
      // (power-user review).
      ...(experimental
        ? [
            {
              label: 'Stories',
              href: ROUTES.DASHBOARD_STORIES,
              icon: <FileTextIcon className="w-5 h-5" />,
              tag: storiesSeen ? undefined : 'New',
            },
            {
              label: 'Ads',
              href: ROUTES.DASHBOARD_ADS,
              icon: <SpeakerLoudIcon className="w-5 h-5" />,
              tag: adsSeen ? undefined : 'New',
            },
            {
              label: 'Slideshows',
              href: ROUTES.DASHBOARD_SLIDESHOWS,
              icon: <StackIcon className="w-5 h-5" />,
              tag: slideshowsSeen ? undefined : 'New',
            },
            {
              label: 'Carousels',
              href: ROUTES.DASHBOARD_CAROUSELS,
              icon: <CardStackIcon className="w-5 h-5" />,
            },
            {
              label: 'Packages',
              href: ROUTES.DASHBOARD_PACKAGES,
              icon: <CubeIcon className="w-5 h-5" />,
            },
          ]
        : []),
      {
        label: 'Team',
        href: ROUTES.DASHBOARD_TEAM,
        icon: <PersonIcon className="w-5 h-5" />,
      },
      {
        label: 'API Keys',
        href: ROUTES.DASHBOARD_API_KEYS,
        icon: <MixerHorizontalIcon className="w-5 h-5" />,
      },
      {
        label: 'Webhooks',
        href: ROUTES.DASHBOARD_WEBHOOKS,
        icon: <LinkBreak2Icon className="w-5 h-5" />,
      },
      {
        label: 'Usage',
        href: ROUTES.DASHBOARD_USAGE,
        icon: <BarChartIcon className="w-5 h-5" />,
      },
      {
        label: 'Billing',
        href: ROUTES.DASHBOARD_BILLING,
        icon: <IdCardIcon className="w-5 h-5" />,
      },
    ],
    [storiesSeen, adsSeen, slideshowsSeen, experimental],
  );

  const isPlatformAdmin = user?.platformRole === 'platform_admin';

  // Storage health — drives the red dot on the "Storage health" sidebar entry.
  // The hook itself short-circuits if the user isn't platform_admin so non-admins
  // don't pay the network cost.
  const { severity: storageSeverity } = useStorageHealth();
  const storageAlertColor =
    storageSeverity === 'red' ? 'destructive' : undefined;

  // Actions (video LoRA) provider-asset health — drives the red dot on the
  // "Actions" sidebar entry. Same admin gate + 60s staleness as the storage
  // health query above; short-circuited for non-admins via `enabled`.
  const { data: actionsHealth } = useQuery({
    queryKey: ['admin-actions-health-dot'],
    queryFn: () => adminService.getProviderAssetsHealth(),
    enabled: isPlatformAdmin,
    staleTime: 60_000,
    retry: 1,
  });
  const actionsAlertColor =
    (actionsHealth?.live_failed_clips ?? 0) > 0 ? 'destructive' : undefined;

  // Pending-team count drives the badge on the Teams nav item. Only fetched
  // for platform admins so regular users don't pay the network cost. 60s
  // refetch keeps it roughly in sync with the moderation surface without
  // hammering the endpoint.
  const { data: pendingTeamsCount } = useQuery({
    queryKey: ['admin-pending-count'],
    queryFn: () => adminService.getPendingCount(),
    enabled: isPlatformAdmin,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const adminMenuItems: MenuItem[] = useMemo(
    () => [
      { label: 'Monitoring', href: ROUTES.ADMIN_MONITORING, icon: <MixerHorizontalIcon className="w-5 h-5" /> },
      // EyeOpenIcon reads "watching" and is the only unused glyph in this list
      // — the moderation console is a read/watch surface, not an action one.
      { label: 'Moderation', href: ROUTES.ADMIN_MODERATION, icon: <EyeOpenIcon className="w-5 h-5" /> },
      {
        label: 'Storage health',
        href: ROUTES.ADMIN_STORAGE_HEALTH,
        icon: <ArchiveIcon className="w-5 h-5" />,
        alertColor: storageAlertColor,
      },
      { label: 'Inference lookup', href: ROUTES.ADMIN_INFERENCES, icon: <MagnifyingGlassIcon className="w-5 h-5" /> },
      {
        label: 'Teams',
        href: ROUTES.ADMIN_TEAMS,
        icon: <PersonIcon className="w-5 h-5" />,
        badgeCount: pendingTeamsCount ?? 0,
      },
      {
        // TargetIcon (not MagicWandIcon — that's reserved for the member
        // Styles nav item, :264 above): the dispatcher MATCHES a prompt to a
        // style, it doesn't create one, and reusing MagicWandIcon here gave a
        // platform admin the identical glyph twice in the same sidebar.
        label: 'Dispatcher',
        href: ROUTES.ADMIN_DISPATCHER,
        icon: <TargetIcon className="w-5 h-5" />,
      },
      { label: 'Users', href: ROUTES.ADMIN_USERS, icon: <PersonIcon className="w-5 h-5" /> },
      { label: 'LoRAs', href: ROUTES.ADMIN_LORAS, icon: <LightningBoltIcon className="w-5 h-5" /> },
      {
        label: 'Actions',
        href: ROUTES.ADMIN_ACTIONS,
        icon: <VideoIcon className="w-5 h-5" />,
        alertColor: actionsAlertColor,
      },
      { label: 'Characters', href: ROUTES.ADMIN_CHARACTERS, icon: <PersonIcon className="w-5 h-5" /> },
      { label: 'Character settings', href: ROUTES.ADMIN_CHARACTER_SETTINGS, icon: <GearIcon className="w-5 h-5" /> },
      ...(experimental
        ? [{ label: 'Stories', href: ROUTES.ADMIN_STORIES, icon: <FileTextIcon className="w-5 h-5" /> }]
        : []),
      { label: 'LLM Models', href: ROUTES.ADMIN_LLM_MODELS, icon: <ChatBubbleIcon className="w-5 h-5" /> },
      { label: 'Usage', href: ROUTES.ADMIN_USAGE, icon: <BarChartIcon className="w-5 h-5" /> },
      { label: 'Invoices', href: ROUTES.ADMIN_INVOICES, icon: <ReaderIcon className="w-5 h-5" /> },
      { label: 'Pricing', href: ROUTES.ADMIN_PRICING, icon: <DashboardIcon className="w-5 h-5" /> },
      { label: 'Templates', href: ROUTES.ADMIN_TEMPLATES, icon: <FileIcon className="w-5 h-5" /> },
      { label: 'Audit log', href: ROUTES.ADMIN_AUDIT, icon: <ActivityLogIcon className="w-5 h-5" /> },
    ],
    [storageAlertColor, actionsAlertColor, pendingTeamsCount, experimental],
  );

  return (
    <motion.aside
      animate={{
        width: isCollapsed ? 64 : 256,
      }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 40,
        mass: 1,
      }}
      className="sticky top-0 h-screen bg-card border-r border-border/40 flex flex-col"
    >
      {/* Logo/Brand */}
      <div className="border-b border-border/40 relative">
        <div
          className={clsx(
            'flex items-center',
            isCollapsed ? 'justify-center flex-col gap-2' : 'justify-between',
          )}
        >
          {isCollapsed ? (
            <div className="py-2 flex flex-col gap-2 items-center">
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/30 transition-all duration-300 rounded-lg cursor-pointer"
                  onClick={toggle}
                  data-sidebar-toggle
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </Button>
              </div>
              <Link to={ROUTES.HOME}>
                <motion.div
                  className="w-8 h-8 bg-primary rounded-lg flex-shrink-0 flex items-center justify-center cursor-pointer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <LightningBoltIcon className="w-5 h-5 text-white" />
                </motion.div>
              </Link>
            </div>
          ) : (
            <div className="flex items-center h-[64px] w-full px-3">
              <Link to={ROUTES.HOME} className="flex items-center cursor-pointer">
                <AnimatePresence>
                  {!isCollapsed && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <PrismWordmark />
                    </motion.div>
                  )}
                </AnimatePresence>
              </Link>

              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="shrink-0 ml-auto pr-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-shrink-0 text-muted-foreground hover:text-foreground hover:bg-secondary/30 transition-all duration-300 rounded-lg cursor-pointer"
                  onClick={toggle}
                  data-sidebar-toggle
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </Button>
              </motion.div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-3 overflow-y-auto">
        {/* Dashboard Category */}
        <div>
          <CategoryHeader
            label="Dashboard"
            isExpanded={expandedCategories.dashboard}
            onToggle={() => toggleCategory('dashboard')}
            isCollapsed={isCollapsed}
          />
          <AnimatePresence>
            {expandedCategories.dashboard && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="space-y-1 mt-1 pl-2">
                  {dashboardMenuItems.map((item) => (
                    <NavItem
                      key={item.href}
                      item={item}
                      isActive={location.pathname === item.href}
                      isCollapsed={isCollapsed}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Admin Category -- platform_admin only */}
        {isPlatformAdmin && (
          <div>
            <CategoryHeader
              label="Admin"
              isExpanded={expandedCategories.admin}
              onToggle={() => toggleCategory('admin')}
              isCollapsed={isCollapsed}
            />
            <AnimatePresence>
              {expandedCategories.admin && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-1 mt-1 pl-2">
                    {adminMenuItems.map((item) => (
                      <NavItem
                        key={item.href}
                        item={item}
                        isActive={location.pathname === item.href}
                        isCollapsed={isCollapsed}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </nav>

      {/* User Profile Section at Bottom */}
      <div className="border-t border-border/40 p-4">
        {/* Light/dark toggle — mirrors the prism-landing switch */}
        <div className={clsx('mb-2 flex', isCollapsed ? 'justify-center' : 'justify-end')}>
          <ThemeToggle />
        </div>
        {/* User Profile Dropdown / Login Button */}
        {isInitializing ? (
          // Show loading state while initializing
          <div className="w-full flex items-center justify-center py-2.5">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
          </div>
        ) : isAuthenticated ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={clsx(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-all duration-200 cursor-pointer',
                  isCollapsed && 'justify-center',
                )}
              >
                <PersonIcon className="w-5 h-5 text-foreground flex-shrink-0" />
                <AnimatePresence>
                  {!isCollapsed && (
                    <motion.span
                      className="text-sm font-medium text-foreground truncate min-w-0"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                      title={user?.email || 'Profile'}
                    >
                      {user?.email || 'Profile'}
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side="top"
              align="start"
              className="min-w-[200px] max-w-[240px]"
            >
              {/* Credits Display in Dropdown */}
              <DropdownMenuItem
                onClick={handleCreditsClick}
                className="cursor-pointer"
              >
                <div className="flex items-center gap-2 w-full">
                  <span className="text-primary font-bold">{formatCredits(parseFloat(String(teamCredits ?? 0)))}</span>
                  <span className="text-xs text-muted-foreground">credits</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleNavigateToProfile}
                className="cursor-pointer"
              >
                <PersonIcon className="w-4 h-4 mr-2" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-400 focus:text-red-400 cursor-pointer"
              >
                <ExitIcon className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            asChild
            className={clsx(
              'w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl cursor-pointer',
              isCollapsed && 'px-2',
            )}
            size="sm"
          >
            <Link to="/auth/register">
              {isCollapsed ? <PersonIcon className="w-4 h-4" /> : 'Sign In'}
            </Link>
          </Button>
        )}
      </div>
    </motion.aside>
  );
};
